/**
 * A lista de organizações da gestão (#765, fase 3, #767).
 *
 * Todas as organizações do CliqCRM, com o plano, os usuários e o armazenamento
 * de cada uma. Só o Super Admin dentro da Cliqfy chega aqui (fase 1, #1013).
 *
 * ── A tela nasceu antes do back ───────────────────────────────────────────
 *
 * Decisão de 26/10/2026: as telas da gestão ficam prontas antes da API, fora
 * do menu, e entram nele quando o back de cada uma chegar. Por isso esta tela
 * recebe tudo pelas props: quem a monta é que consulta a API e guarda a busca
 * e o filtro.
 *
 * ── Quem chega a 90% ou ao limite fica à vista ────────────────────────────
 *
 * O medidor muda de cor e diz a porcentagem: é por ele que a equipe vê, de
 * relance, quem está para estourar o contrato.
 */

import type { ReactNode } from 'react';
import {
  Avatar,
  Botao,
  CabecalhoDeTela,
  Campo,
  FiltroPilula,
  Icone,
  Medidor,
  Segmentado,
  Selo,
  Tabela,
  type ColunaTabela,
  type OpcaoSegmentada,
} from '@/shared/ui';
import { comUmaCasa, diaMesAno } from '@/shared/lib/formato';
import { mascararCnpj } from '@/shared/lib/cnpj';
import { Carregando, Erro, Vazio } from '@/shared/ui/estados/estados';
import {
  ORDEM_DAS_SITUACOES,
  SITUACOES,
  type ContagensDaLista,
  type OrganizacaoNaLista,
  type SituacaoDaOrganizacao,
} from '../model/organizacao-na-lista';
import { rotuloDoArmazenamento, rotuloDosUsuarios } from '../lib/formato-da-lista';
import './organizacoes.css';

/** As duas partes da gestão, nas abas do topo. */
export type AbaDaGestao = 'organizacoes' | 'planos';

export type TelaDeOrganizacoesProps = {
  /** O que veio do servidor; sem nada enquanto carrega. */
  organizacoes: readonly OrganizacaoNaLista[] | undefined;
  contagens: ContagensDaLista | undefined;
  /** O erro da consulta, quando ela falhou e não há o que mostrar. */
  erro?: unknown;
  aoRepetir: () => void;
  /**
   * A lista na tela ainda é a de outra busca: a nova espera a digitação parar
   * ou a resposta chegar (#1160). Enquanto isso, o vazio não é o de primeira
   * vez, que piscaria ao apagar uma busca sem resultado.
   */
  atualizando?: boolean;
  busca: string;
  aoBuscar: (texto: string) => void;
  /** A situação escolhida no filtro; nula é "Todas". */
  situacao: SituacaoDaOrganizacao | null;
  aoFiltrar: (situacao: SituacaoDaOrganizacao | null) => void;
  aoAbrir: (id: string) => void;
  aoCriar: () => void;
  aoTrocarDeAba: (aba: AbaDaGestao) => void;
};

const ABAS: OpcaoSegmentada<AbaDaGestao>[] = [
  { valor: 'organizacoes', rotulo: 'Organizações' },
  { valor: 'planos', rotulo: 'Planos' },
];

const COLUNAS: ColunaTabela[] = [
  { chave: 'organizacao', rotulo: 'Organização' },
  { chave: 'plano', rotulo: 'Plano' },
  { chave: 'usuarios', rotulo: 'Usuários' },
  { chave: 'armazenamento', rotulo: 'Armazenamento' },
  { chave: 'situacao', rotulo: 'Situação' },
  { chave: 'desde', rotulo: 'Desde' },
];

export function TelaDeOrganizacoes({
  organizacoes,
  contagens,
  erro,
  aoRepetir,
  atualizando = false,
  busca,
  aoBuscar,
  situacao,
  aoFiltrar,
  aoAbrir,
  aoCriar,
  aoTrocarDeAba,
}: TelaDeOrganizacoesProps): ReactNode {
  const buscando = busca.trim() !== '';
  return (
    <div className="organizacoes">
      <CabecalhoDeTela
        titulo="Organizações"
        contagem={contagens ? String(contagens.total) : undefined}
        descricaoDaContagem="Organizações no CliqCRM"
        apoio="Todas as organizações do CliqCRM, com o contrato e o uso de cada uma."
        acoes={
          <Botao variante="primario" onClick={aoCriar}>
            <Icone nome="criar" />
            Nova organização
          </Botao>
        }
      />
      <div className="organizacoes__corpo">
        <Segmentado
          rotulo="Partes da gestão"
          aparencia="sublinhado"
          opcoes={ABAS}
          escolhida="organizacoes"
          aoEscolher={aoTrocarDeAba}
        />
        <div role="tabpanel" aria-label="Organizações" className="organizacoes__painel">
          <div className="organizacoes__filtros">
            <div className="organizacoes__busca">
              <Campo
                type="search"
                icone="buscar"
                rotuloOculto="Buscar por nome, identificador ou CNPJ"
                placeholder="Buscar por nome, identificador ou CNPJ"
                value={busca}
                onChange={(evento) => aoBuscar(evento.target.value)}
              />
            </div>
            <div className="organizacoes__situacoes" role="group" aria-label="Situação">
              <FiltroPilula
                ligado={situacao === null}
                aoAlternar={() => aoFiltrar(null)}
                contagem={contagens?.encontradas}
              >
                {buscando ? 'Encontradas' : 'Todas'}
              </FiltroPilula>
              {ORDEM_DAS_SITUACOES.map((valor) => (
                <FiltroPilula
                  key={valor}
                  ligado={situacao === valor}
                  aoAlternar={() => aoFiltrar(situacao === valor ? null : valor)}
                  contagem={contagens?.porSituacao[valor]}
                >
                  {SITUACOES[valor].filtro}
                </FiltroPilula>
              ))}
            </div>
          </div>
          <Conteudo
            organizacoes={organizacoes}
            erro={erro}
            aoRepetir={aoRepetir}
            filtrando={buscando || situacao !== null || atualizando}
            aoLimpar={() => {
              aoBuscar('');
              aoFiltrar(null);
            }}
            aoAbrir={aoAbrir}
            aoCriar={aoCriar}
          />
        </div>
      </div>
    </div>
  );
}

function Conteudo({
  organizacoes,
  erro,
  aoRepetir,
  filtrando,
  aoLimpar,
  aoAbrir,
  aoCriar,
}: {
  organizacoes: readonly OrganizacaoNaLista[] | undefined;
  erro: unknown;
  aoRepetir: () => void;
  filtrando: boolean;
  aoLimpar: () => void;
  aoAbrir: (id: string) => void;
  aoCriar: () => void;
}): ReactNode {
  if (erro && !organizacoes) return <Erro erro={erro} aoRepetir={aoRepetir} />;
  if (!organizacoes) {
    return <Carregando forma="lista" linhas={6} rotulo="Carregando as organizações" />;
  }
  if (organizacoes.length === 0) {
    // Vazio que explica: "não tem nenhuma" e "a busca escondeu todas" são dias diferentes.
    return filtrando ? (
      <Vazio
        titulo="Nenhuma organização encontrada"
        porque="A busca ou o filtro escondeu todas. Limpe os dois para ver a lista inteira."
        acao={{ rotulo: 'Limpar a busca e o filtro', aoClicar: aoLimpar }}
      />
    ) : (
      <Vazio
        titulo="Nenhuma organização ainda"
        porque="As organizações criadas aparecem aqui, com o contrato e o uso de cada uma."
        acao={{ rotulo: 'Nova organização', aoClicar: aoCriar }}
      />
    );
  }
  return (
    <Tabela
      entrada
      rotulo="Organizações"
      classeDaTabela="organizacoes__tabela"
      colunas={COLUNAS}
      linhas={organizacoes.map((organizacao, posicao) => linhaDa(organizacao, posicao, aoAbrir))}
      chaveDe={(linha) => String(linha.id)}
      aoAbrirLinha={(linha) => aoAbrir(String(linha.id))}
    />
  );
}

/**
 * Uma linha da tabela. O nome é um botão de verdade: a linha inteira abre com
 * o mouse, e quem usa teclado precisa de um controle que faça o mesmo.
 */
function linhaDa(
  organizacao: OrganizacaoNaLista,
  posicao: number,
  aoAbrir: (id: string) => void,
): Record<string, ReactNode> {
  const { usuarios, armazenamento } = organizacao;
  const situacao = SITUACOES[organizacao.situacao];
  return {
    id: organizacao.id,
    organizacao: (
      <span className="organizacoes__org">
        <Avatar nome={organizacao.nome} tamanho={32} />
        <span className="organizacoes__quem">
          <button
            type="button"
            className="organizacoes__nome"
            onClick={() => aoAbrir(organizacao.id)}
          >
            {organizacao.nome}
          </button>
          <span className="organizacoes__identificador">
            {organizacao.identificador}
            {organizacao.cnpj ? ` · CNPJ ${mascararCnpj(organizacao.cnpj)}` : null}
          </span>
          {organizacao.situacao === 'em_criacao' && organizacao.parouEm ? (
            <span className="organizacoes__parou">Parou no {organizacao.parouEm}</span>
          ) : null}
        </span>
      </span>
    ),
    plano: organizacao.plano ?? <span className="tabela__sem">Sem contrato</span>,
    usuarios:
      usuarios.contratados === null ? (
        // Sem contrato não há limite: o que se sabe é quantos usam.
        <span className="organizacoes__contratado">{usuarios.emUso} em uso</span>
      ) : (
        <Medidor
          nome="Usuários"
          usado={usuarios.emUso}
          total={usuarios.contratados}
          rotulo={rotuloDosUsuarios(usuarios.emUso, usuarios.contratados)}
          posicao={posicao}
        />
      ),
    armazenamento:
      armazenamento.contratadoGb === null ? (
        <span className="tabela__sem">Sem contrato</span>
      ) : armazenamento.emUsoGb === null ? (
        // Sem a medição (#716), o que se sabe é o contratado: nada de barra vazia fingindo zero.
        <span className="organizacoes__contratado">
          {comUmaCasa(armazenamento.contratadoGb)} GB contratados
        </span>
      ) : (
        <Medidor
          nome="Armazenamento"
          usado={armazenamento.emUsoGb}
          total={armazenamento.contratadoGb}
          rotulo={rotuloDoArmazenamento(armazenamento.emUsoGb, armazenamento.contratadoGb)}
          posicao={posicao}
        />
      ),
    situacao: <Selo tom={situacao.tom}>{situacao.rotulo}</Selo>,
    desde: <span className="organizacoes__data">{diaMesAno(organizacao.desde)}</span>,
  };
}
